// src/utils/chartFormat.js
// Helper format bucket tanggal/waktu untuk chart time-series
// (dipakai TrafficChartWidget & ConcurrentWidget).
//
// Bucket dari backend bisa berupa:
//   - "YYYY-MM-DD"             → granularitas harian (mis. filter period 7d/30d)
//   - "YYYY-MM-DD HH:MM:SS"    → granularitas jam (mis. filter period today)
//
// BUG LAMA: bucket harian ("YYYY-MM-DD" tanpa jam) diparse `new Date()` sebagai
// UTC 00:00, lalu dirender pakai jam LOKAL browser → di WIB (UTC+7) semua tick
// jadi "07.00" karena UTC 00:00 selalu = 07:00 WIB, apa pun tanggalnya.
// Fix: deteksi dulu apakah bucket itu tanggal-only atau ada jamnya.
export function fmtBucket(v) {
  if (!v) return '';
  const str = String(v).trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
  const d = new Date(str.replace(' ', 'T'));
  if (isNaN(d)) return v;

  return isDateOnly
    // Tanggal-only → tampilkan sebagai tanggal, pakai UTC supaya tidak ikut geser.
    ? d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })
    // Ada komponen jam → tampilkan jam:menit lokal (perilaku lama, tetap dipakai).
    : d.toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ── Gap-fill bucket time-series ──────────────────────────────────────────────
// Backend (ClickHouse) hanya mengembalikan bucket yang ADA barisnya. Untuk
// concurrent user, bucket tanpa sesi aktif = 0 user, bukan "tidak ada data".
// Tanpa gap-fill, chart menyambung garis lurus dari bucket sebelum ke sesudah
// gap (kelihatan seperti masih ada 1 user padahal 0), sumbu-X tidak proporsional,
// dan min/avg terlihat terlalu tinggi karena bucket 0 tidak ikut dihitung.
const GRANULARITY_MS = { '1m': 60e3, '5m': 300e3, '15m': 900e3, '30m': 1800e3, hour: 3600e3, '1h': 3600e3, day: 86400e3, '1d': 86400e3, week: 604800e3 };

// Parse & format selalu dalam UTC supaya round-trip persis (tanpa geser zona/DST);
// string yang dihasilkan berformat sama dengan input sehingga fmtBucket tetap jalan.
const parseBucketMs = (b) => {
  const s = String(b).trim();
  return Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : `${s.replace(' ', 'T')}Z`);
};
const pad2 = (n) => String(n).padStart(2, '0');
function formatBucketMs(ms, dateOnly) {
  const d = new Date(ms);
  const date = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return dateOnly ? date : `${date} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`;
}

// Kembalikan daftar bucket LENGKAP (terurut, tanpa bolong). Kalau step tidak bisa
// ditentukan / data tidak selaras dengan step / hasil terlalu besar → kembalikan
// daftar asli (terurut) apa adanya, jadi aman dipanggil di mana saja.
// `granularity` opsional ('5m' | 'hour' | 'day' | 'week'); tanpa itu step diambil
// dari selisih terkecil antar bucket.
export function fillBucketGaps(buckets, { granularity, maxPoints = 5000 } = {}) {
  const uniq = [...new Set((buckets || []).map(String))];
  const parsed = uniq.map(b => ({ b, ms: parseBucketMs(b) }));
  if (parsed.some(p => isNaN(p.ms))) return uniq;
  parsed.sort((a, b) => a.ms - b.ms);
  if (parsed.length < 2) return parsed.map(p => p.b);

  const aligned = (step) => parsed.every(p => (p.ms - parsed[0].ms) % step === 0);
  let step = GRANULARITY_MS[granularity];
  if (!step || !aligned(step)) {
    step = Infinity;
    for (let i = 1; i < parsed.length; i++) step = Math.min(step, parsed[i].ms - parsed[i - 1].ms);
    if (!isFinite(step) || step <= 0 || !aligned(step)) return parsed.map(p => p.b);
  }

  const first = parsed[0].ms, last = parsed[parsed.length - 1].ms;
  if ((last - first) / step + 1 > maxPoints) return parsed.map(p => p.b);

  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(parsed[0].b);
  const out = [];
  for (let t = first; t <= last; t += step) out.push(formatBucketMs(t, dateOnly));
  return out;
}